/* eslint-disable react/no-unknown-property */
'use client';

import React, { useEffect, useRef, useState } from 'react';
import { Canvas, extend, useFrame } from '@react-three/fiber';
import { useGLTF, useTexture, Environment, Lightformer, AdaptiveDpr, PerformanceMonitor } from '@react-three/drei';
import { MeshLine, MeshLineMaterial } from 'meshline';
import {
  BallCollider,
  CuboidCollider,
  Physics,
  RigidBody,
  useRopeJoint,
  useSphericalJoint,
  RigidBodyProps
} from '@react-three/rapier';
import * as THREE from 'three';

import cardGLB from '../assets/lanyard/card.glb';
import lanyardTex from '../assets/lanyard/lanyard.png';
import './Lanyard.css';

extend({ MeshLine, MeshLineMaterial });

interface LanyardProps {
  position?: [number, number, number];
  gravity?: [number, number, number];
  fov?: number;
  transparent?: boolean;
  onUnlock?: () => void;
  unlockDistance?: number;
  cooldownMs?: number;
  activateDistance?: number; // minimal drag distance to start showing HUD/moving
  anchorY?: number; // rope top anchor Y
  stickToTop?: boolean; // auto place anchor at top edge
  topOffset?: number; // margin from top (world units) - if provided, takes precedence over px
  topOffsetPx?: number; // margin from top in pixels, converted to world using container height
  cardAttachY?: number; // local Y on the card where the rope attaches
  hudTopPx?: number; // vertical offset for HUD text in pixels
  compatMode?: boolean; // match upstream defaults for smoother perf
  autoHideOnScroll?: boolean; // auto hide when scrolled past first screen
  hideScrollThreshold?: number; // scroll distance in vh to trigger hide (default: 80)
}

export const Lanyard: React.FC<LanyardProps> = ({
  position = [0, 0, 30],
  gravity = [0, -40, 0],
  fov = 20,
  transparent = true,
  onUnlock,
  unlockDistance = 7,
  cooldownMs = 3000,
  activateDistance = 0.15,
  anchorY = 3,
  stickToTop = false,
  topOffset = undefined,
  topOffsetPx = 44,
  cardAttachY = 1.45,
  hudTopPx = 8,
  compatMode = true,
  autoHideOnScroll = false,
  hideScrollThreshold = 80
}) => {
  const [progress, setProgress] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(400); // baseline 400
  const [containerHeight, setContainerHeight] = useState<number>(300);
  const [lowPerf, setLowPerf] = useState(false);
  const [isHidden, setIsHidden] = useState(false);

  // Auto-hide on scroll effect
  useEffect(() => {
    if (!autoHideOnScroll) return;
    
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const threshold = (window.innerHeight * hideScrollThreshold) / 100;
      setIsHidden(scrollY > threshold);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Check initial state
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [autoHideOnScroll, hideScrollThreshold]);

  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new (window as any).ResizeObserver((entries: any[]) => {
      const w = entries[0]?.contentRect?.width;
      const h = entries[0]?.contentRect?.height;
      if (w) setContainerWidth(w);
      if (h) setContainerHeight(h);
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  // Keep card visual size roughly constant by adapting FOV to container width
  const baseWidth = 400;
  const effectiveFov = fov * (containerWidth / baseWidth);
  const halfFovRad = (effectiveFov * Math.PI) / 180 / 2;
  const halfHeightAtZ = Math.tan(halfFovRad) * Math.abs(position[2]);
  // If px offset provided, convert px -> world units at z=0
  const pxToWorldY = (px: number) => (px / Math.max(1, containerHeight)) * (2 * halfHeightAtZ);
  const topOffsetWorld = topOffset != null ? topOffset : pxToWorldY(topOffsetPx);
  const computedAnchorY = stickToTop ? halfHeightAtZ - topOffsetWorld : anchorY;

  const handleUnlocked = () => {
    setShowConfetti(true);
    onUnlock?.();
    setTimeout(() => setShowConfetti(false), 1500);
  };

  return (
    <div className={`lanyard-wrapper ${isHidden ? 'lanyard-hidden' : ''}`} ref={containerRef}>
      <div className="lanyard-hud" aria-hidden="true" style={{ top: hudTopPx }}>
        <svg className="lanyard-ring" viewBox="0 0 120 120">
          <circle className="ring-bg" cx="60" cy="60" r="52" />
          <circle
            className="ring-fg"
            cx="60"
            cy="60"
            r="52"
            strokeDasharray={Math.PI * 2 * 52}
            strokeDashoffset={(1 - progress) * Math.PI * 2 * 52}
          />
          <text x="60" y="66" textAnchor="middle" className="ring-text">
            {Math.round(progress * 100)}%
          </text>
        </svg>
        <div className="lanyard-tip">{progress >= 1 ? 'Release to start quiz' : 'Pull to start quiz'}</div>
        {showConfetti && <ConfettiBurst />}
      </div>
      <Canvas
        camera={{ position, fov: effectiveFov }}
        dpr={compatMode ? undefined : (lowPerf ? 1 : [1, 1.5])}
        shadows={false}
        gl={compatMode ? { alpha: transparent } : { alpha: transparent, antialias: false, powerPreference: 'low-power' }}
        onCreated={({ gl }: { gl: THREE.WebGLRenderer }) =>
          gl.setClearColor(new THREE.Color(0x000000), transparent ? 0 : 1)
        }
      >
        {!compatMode && (
          <>
            <PerformanceMonitor onDecline={() => setLowPerf(true)} onIncline={() => setLowPerf(false)} />
            <AdaptiveDpr pixelated />
          </>
        )}
        <ambientLight intensity={Math.PI} />
        <Physics gravity={gravity} timeStep={1 / 60}>
          <Band
            onProgress={setProgress}
            onUnlocked={handleUnlocked}
            unlockDistance={unlockDistance}
            cooldownMs={cooldownMs}
            activateDistance={activateDistance}
            anchorY={computedAnchorY}
            lowPerf={lowPerf}
            cardAttachY={cardAttachY}
            compatMode={compatMode}
          />
          {/* activateDistance is forwarded via physics band */}
        </Physics>
        {(compatMode || !lowPerf) && (
          <Environment blur={0.75}>
            <Lightformer
              intensity={2}
              color="white"
              position={[0, -1, 5]}
              rotation={[0, 0, Math.PI / 3]}
              scale={[100, 0.1, 1]}
            />
            <Lightformer
              intensity={3}
              color="white"
              position={[-1, -1, 1]}
              rotation={[0, 0, Math.PI / 3]}
              scale={[100, 0.1, 1]}
            />
            <Lightformer
              intensity={3}
              color="white"
              position={[1, 1, 1]}
              rotation={[0, 0, Math.PI / 3]}
              scale={[100, 0.1, 1]}
            />
            <Lightformer
              intensity={10}
              color="white"
              position={[-10, 0, 14]}
              rotation={[0, Math.PI / 2, Math.PI / 3]}
              scale={[100, 10, 1]}
            />
          </Environment>
        )}
      </Canvas>
    </div>
  );
};

interface BandProps {
  maxSpeed?: number;
  minSpeed?: number;
  onProgress?: (p: number) => void;
  onUnlocked?: () => void;
  unlockDistance?: number;
  cooldownMs?: number;
  activateDistance?: number;
  anchorY?: number;
  ropeLength?: number;
  cardAttachY?: number;
  lowPerf?: boolean;
  compatMode?: boolean;
}

function Band({
  maxSpeed = 50,
  minSpeed = 0,
  onProgress,
  onUnlocked,
  unlockDistance = 7,
  cooldownMs = 3000,
  activateDistance = 0.15,
  anchorY = 3,
  ropeLength = 2.4,
  cardAttachY = 1.45,
  lowPerf = false,
  compatMode = true
}: BandProps) {
  const band = useRef<any>(null);
  const fixed = useRef<any>(null);
  const j1 = useRef<any>(null);
  const j2 = useRef<any>(null);
  const j3 = useRef<any>(null);
  const card = useRef<any>(null);

  const vec = new THREE.Vector3();
  const ang = new THREE.Vector3();
  const rot = new THREE.Vector3();
  const dir = new THREE.Vector3();

  const segmentProps: any = {
    type: 'dynamic' as RigidBodyProps['type'],
    canSleep: true,
    colliders: false,
    angularDamping: 6, // stronger damping to reduce rope wobble
    linearDamping: 6
  };

  const { nodes, materials } = useGLTF(cardGLB) as any;
  const texture = useTexture(lanyardTex);

  const [curve] = useState(
    () =>
      new THREE.CatmullRomCurve3([new THREE.Vector3(), new THREE.Vector3(), new THREE.Vector3(), new THREE.Vector3()])
  );

  const [dragged, drag] = useState<false | THREE.Vector3>(false);
  const [hovered, hover] = useState(false);
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const draggedSinceDownRef = useRef(false);
  const dragStartCardPosRef = useRef<THREE.Vector3 | null>(null);
  const [isSmall, setIsSmall] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth < 1024;
    }
    return false;
  });
  const frameSkipRef = useRef(0);

  useEffect(() => {
    const handleResize = (): void => {
      setIsSmall(window.innerWidth < 1024);
    };
    window.addEventListener('resize', handleResize);
    return (): void => window.removeEventListener('resize', handleResize);
  }, []);

  const segLen = ropeLength / 3;
  useRopeJoint(fixed, j1, [[0, 0, 0], [0, 0, 0], segLen]);
  useRopeJoint(j1, j2, [[0, 0, 0], [0, 0, 0], segLen]);
  useRopeJoint(j2, j3, [[0, 0, 0], [0, 0, 0], segLen]);
  useSphericalJoint(j3, card, [
    [0, 0, 0],
    [0, cardAttachY, 0]
  ]);

  useEffect(() => {
    if (hovered) {
      document.body.style.cursor = dragged ? 'grabbing' : 'grab';
      return () => {
        document.body.style.cursor = 'auto';
      };
    }
  }, [hovered, dragged]);

  useFrame((state: any, delta: number) => {
    if (dragged && typeof dragged !== 'boolean') {
      draggedSinceDownRef.current = true;
      vec.set(state.pointer.x, state.pointer.y, 0.5).unproject(state.camera);
      dir.copy(vec).sub(state.camera.position).normalize();
      vec.add(dir.multiplyScalar(state.camera.position.length()));
      [card, j1, j2, j3, fixed].forEach(ref => ref.current?.wakeUp());

      // ensure minimal movement before we start moving the card or showing HUD
      if (dragStartCardPosRef.current == null) {
        dragStartCardPosRef.current = new THREE.Vector3().copy(card.current.translation());
      }
      const desired = new THREE.Vector3(vec.x - (dragged as THREE.Vector3).x, vec.y - (dragged as THREE.Vector3).y, vec.z - (dragged as THREE.Vector3).z);
      const deltaFromStart = desired.distanceTo(dragStartCardPosRef.current);
      if (deltaFromStart >= activateDistance) {
        card.current?.setNextKinematicTranslation({
          x: desired.x,
          y: desired.y,
          z: desired.z
        });
      } else {
        // do not move until surpass threshold
        onProgress?.(0);
      }
    }

    if (fixed.current) {
      [j1, j2].forEach(ref => {
        if (!ref.current.lerped) ref.current.lerped = new THREE.Vector3().copy(ref.current.translation());
        const clampedDistance = Math.max(0.1, Math.min(1, ref.current.lerped.distanceTo(ref.current.translation())));
        // frame-rate independent smoothing (avoids jelly effect on low FPS)
        const smoothingRate = (minSpeed + clampedDistance * (maxSpeed - minSpeed));
        const alpha = 1 - Math.exp(-smoothingRate * Math.max(0.0001, delta));
        ref.current.lerped.lerp(ref.current.translation(), Math.min(1, alpha));
      });

      curve.points[0].copy(j3.current.translation());
      curve.points[1].copy(j2.current.lerped);
      curve.points[2].copy(j1.current.lerped);
      curve.points[3].copy(fixed.current.translation());

      // Throttle geometry updates a bit in lowPerf to reduce meshline churn
      if (!lowPerf || (frameSkipRef.current++ % 2 === 0)) {
        band.current.geometry.setPoints(curve.getPoints(compatMode ? 32 : (lowPerf ? 12 : 20)));
      }

      ang.copy(card.current.angvel());
      rot.copy(card.current.rotation());
      card.current.setAngvel({ x: ang.x, y: ang.y - (rot.y as number) * 0.25, z: ang.z });

      // progress only when actively dragging; otherwise decay to 0
      if (dragged && typeof dragged !== 'boolean' && draggedSinceDownRef.current && dragStartCardPosRef.current) {
        const top = fixed.current.translation();
        const c = card.current.translation();
        const dist = Math.max(0, top.y - c.y);
        const p = Math.min(1, dist / unlockDistance);
        onProgress?.(p);
      } else {
        onProgress?.(0);
      }
    }
  });

  curve.curveType = 'chordal';
  texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
  texture.anisotropy = compatMode ? 1 : 8;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = compatMode ? THREE.LinearFilter : THREE.NearestFilter;
  texture.generateMipmaps = true;
  texture.needsUpdate = true;

  return (
    <>
      <group position={[0, anchorY, 0]}>
        <RigidBody ref={fixed} {...segmentProps} type={'fixed' as RigidBodyProps['type']} ccd={!compatMode}>
        </RigidBody>
        <RigidBody position={[0, -segLen, 0]} ref={j1} {...segmentProps} type={'dynamic' as RigidBodyProps['type']} ccd={!compatMode}>
          <BallCollider args={[0.1]} />
        </RigidBody>
        <RigidBody position={[0, -segLen * 2, 0]} ref={j2} {...segmentProps} type={'dynamic' as RigidBodyProps['type']} ccd={!compatMode}>
          <BallCollider args={[0.1]} />
        </RigidBody>
        <RigidBody position={[0, -segLen * 3, 0]} ref={j3} {...segmentProps} type={'dynamic' as RigidBodyProps['type']} ccd={!compatMode}>
          <BallCollider args={[0.1]} />
        </RigidBody>

        <RigidBody
          position={[2, 0, 0]}
          ref={card}
          {...segmentProps}
          type={dragged ? ('kinematicPosition' as RigidBodyProps['type']) : ('dynamic' as RigidBodyProps['type'])}
          ccd={!compatMode}
        >
          <CuboidCollider args={[0.8, 1.125, compatMode ? 0.01 : 0.02]} />
          <group
            scale={3.6}
            position={[0, -1.2, -0.05]}
            onPointerOver={() => hover(true)}
            onPointerOut={() => hover(false)}
            onPointerUp={(e: any) => {
              e.target.releasePointerCapture(e.pointerId);
              drag(false);
              if (fixed.current && Date.now() > cooldownUntil && draggedSinceDownRef.current) {
                const top = fixed.current.translation();
                const c = card.current.translation();
                const dist = Math.max(0, top.y - c.y);
                if (dist >= (unlockDistance as number)) {
                  setCooldownUntil(Date.now() + cooldownMs);
                  card.current.applyImpulse({ x: 0, y: 6, z: 0 }, true);
                  onUnlocked?.();
                }
              }
              draggedSinceDownRef.current = false;
              dragStartCardPosRef.current = null;
            }}
            onPointerDown={(e: any) => {
              e.target.setPointerCapture(e.pointerId);
              draggedSinceDownRef.current = false;
              drag(new THREE.Vector3().copy(e.point).sub(vec.copy(card.current.translation())));
              dragStartCardPosRef.current = new THREE.Vector3().copy(card.current.translation());
            }}
          >
            <mesh geometry={nodes.card.geometry}>
              <meshPhysicalMaterial
                map={materials.base.map}
                map-anisotropy={16}
                clearcoat={1}
                clearcoatRoughness={0.15}
                roughness={0.9}
                metalness={0.8}
              />
            </mesh>
            <mesh geometry={nodes.clip.geometry} material={materials.metal} material-roughness={0.3} />
            <mesh geometry={nodes.clamp.geometry} material={materials.metal} />
          </group>
        </RigidBody>
      </group>

      <mesh ref={band}>
        {/* @ts-expect-error meshline intrinsic not typed */}
        <meshLine />
        {/* @ts-expect-error meshline intrinsic not typed */}
        <meshLineMaterial
          color="white"
          depthTest={false}
          transparent
          depthWrite={false}
          resolution={isSmall ? [1000, 2000] : [1000, 1000]}
          useMap
          map={texture as any}
          repeat={[-1, 1] as any}
          lineWidth={0.22 as any}
        />
      </mesh>
    </>
  );
}

// Simple DOM confetti burst
const ConfettiBurst: React.FC = () => {
  const pieces = Array.from({ length: 18 });
  return (
    <div className="confetti-burst">
      {pieces.map((_, i) => (
        <span key={i} className="confetti-piece" />
      ))}
    </div>
  );
};
